import { useEffect, useRef, useState } from "react";
import { Palette, Download, Upload, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { useSociete } from "../../contexts/SocieteContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ThemeGallery({ api, societeId, config, onApplied }) {
  const { societes } = useSociete();
  const [themes, setThemes] = useState([]);
  const [busy, setBusy] = useState("");
  const [copyFrom, setCopyFrom] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/societe-builder/themes").then((r) => setThemes(r.data.themes || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = async (body, key) => {
    setBusy(key);
    try { const r = await api.post(`/societes/${societeId}/builder/apply-theme`, body); toast.success(r.data.message); onApplied?.(r.data.config); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };
  const exportTheme = async () => {
    try {
      const r = await api.get(`/societes/${societeId}/builder/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `theme-${config.slug || societeId}.json`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export impossible"); }
  };
  const importTheme = async (file) => {
    if (!file) return;
    setBusy("import");
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post(`/societes/${societeId}/builder/import`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(r.data.message); onApplied?.(r.data.config); }
    catch (e) { toast.error(e.response?.data?.detail || "Import impossible"); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };
  const current = config?.branding?.primary?.toLowerCase();

  return (
    <div className="rounded-xl border border-white/10 bg-[#0b0811] p-4 space-y-3" data-testid="theme-gallery">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs font-bold text-slate-200 flex items-center gap-1.5"><Palette className="w-4 h-4 text-[#f7c935]" /> Thèmes prêts à l'emploi</div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={exportTheme} className="h-7 text-xs border-white/10" data-testid="theme-export-btn"><Download className="w-3 h-3 mr-1" /> Exporter (JSON)</Button>
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy === "import"} className="h-7 text-xs border-white/10" data-testid="theme-import-btn">
            {busy === "import" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />} Importer un thème
          </Button>
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={(e) => importTheme(e.target.files?.[0])} data-testid="theme-import-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {themes.map((t) => {
          const b = t.branding; const active = current && b.primary?.toLowerCase() === current;
          return (
            <button key={t.theme_id} onClick={() => apply({ theme_id: t.theme_id }, t.theme_id)} disabled={!!busy}
              className={`text-left rounded-lg border p-3 transition-colors hover:border-[#f7c935]/50 ${active ? "border-[#f7c935]" : "border-white/10"}`} style={{ background: b.background }} data-testid={`theme-card-${t.theme_id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 rounded-full" style={{ background: b.primary }} />
                <span className="w-3 h-3 rounded-full" style={{ background: b.secondary }} />
                {b.logo_url && <img src={`${BACKEND}${b.logo_url}`} alt="" className="h-5 ml-auto object-contain" />}
                {active && <Check className="w-4 h-4 ml-auto" style={{ color: b.primary }} />}
              </div>
              <div className="text-xs font-bold" style={{ color: b.login_theme === "light" ? "#fff" : "#fff" }}>{busy === t.theme_id ? "Application…" : t.name}</div>
              <div className="text-[10px] text-slate-400 leading-tight mt-0.5">{t.description}</div>
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 text-xs">
        <Copy className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-slate-400">Copier le thème d'une autre société :</span>
        <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} className="bg-[#14101c] border border-white/10 text-white rounded px-2 py-1 text-xs" data-testid="theme-copy-from">
          <option value="">— choisir —</option>
          {societes.filter((s) => s.societe_id !== societeId).map((s) => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
        </select>
        <Button size="sm" onClick={() => apply({ from_societe_id: copyFrom }, "copy")} disabled={!copyFrom || !!busy} className="h-7 text-xs bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="theme-copy-btn">Copier</Button>
      </div>
      <p className="text-[10px] text-slate-500">Le thème s'applique à la page de connexion /login/{config.slug || "…"}, au panel de la société et aux apps. Enregistrement immédiat.</p>
    </div>
  );
}
